import React from "react";
import { 
  Box, 
  Modal, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip,
  Avatar,
  useTheme 
} from "@mui/material";
import { Close } from "@mui/icons-material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "95%",
  maxWidth: 800,
  bgcolor: "background.paper",
  borderRadius: 4,
  boxShadow: 24,
  p: 2,
  maxHeight: "90vh",
  overflow: "hidden",
};

const AuctionDetails = ({ auction, open, setOpen }) => {
  const theme = useTheme();

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      aria-labelledby="auction-details-modal"
    >
      <Box sx={modalStyle}>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center",
          mb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          pb: 2
        }}>
          <Typography variant="h5" fontWeight="600">
            تفاصيل المزاد
          </Typography>
          <Close 
            onClick={() => setOpen(false)} 
            sx={{ 
              cursor: "pointer", 
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.text.primary }
            }} 
          />
        </Box>

        <Box sx={{ 
          maxHeight: "calc(90vh - 100px)", 
          overflowY: "auto",
          px: 1,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": { bgcolor: theme.palette.divider, borderRadius: 3 }
        }}>
          {/* Image Section */}
          <Box sx={{ 
            textAlign: "center", 
            mb: 4,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: 3,
            position: "relative",
            bgcolor: theme.palette.background.neutral
          }}>
            {auction?.image_url ? (
              <img
                src={process.env.REACT_APP_IMAGE_URL + auction.image_url}
                alt={auction.title || "صورة المزاد"}
                style={{
                  width: "100%",
                  maxHeight: "300px",
                  objectFit: "cover",
                  transition: "transform 0.3s ease",
                }}
              />
            ) : (
              <Box sx={{ py: 8 }}>
                <Typography variant="body1" color="text.secondary">
                  لا توجد صورة متوفرة
                </Typography>
              </Box>
            )}
          </Box>

          {/* Details Grid */}
          <Box sx={{ 
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 3,
            mb: 4
          }}>
            <DetailItem 
              label="اسم المزاد" 
              value={auction?.title} 
              icon="📝"
            />
            <DetailItem 
              label="صاحب المزاد" 
              value={auction?.user?.name} 
              icon="👤"
            />
            <DetailItem 
              label="بداية المزاد" 
              value={auction?.startDate} 
              icon="⏳"
            />
            <DetailItem 
              label="نهاية المزاد" 
              value={auction?.expiryDate} 
              icon="⌛️"
            />
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Chip
                label={auction?.confirmed ? "مفعل" : "غير مفعل"}
                color={auction?.confirmed ? "success" : "error"}
                variant="outlined"
                sx={{ 
                  borderRadius: 1,
                  borderWidth: 2,
                  fontWeight: 600,
                  textTransform: "uppercase"
                }}
              />
            </Box>
          </Box>

          {/* Products Table */}
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            سلع المزاد
          </Typography>
          {auction?.auction_products?.length > 0 ? (
            <TableContainer 
              component={Paper} 
              sx={{ 
                boxShadow: 'none',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden"
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ bgcolor: theme.palette.background.neutral }}>
                  <TableRow>
                    {["المنتج", "حد السوم", "فرق السوم", "فتح الباب"].map((header) => (
                      <TableCell 
                        key={header}
                        sx={{ 
                          fontWeight: 600, 
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auction.auction_products.map((product, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:last-child td': { borderBottom: 0 },
                        '&:hover': { bgcolor: theme.palette.action.hover }
                      }}
                    >
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        {product.product}
                      </TableCell>
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        {product.actualPrice}
                      </TableCell>
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        {product.bidPrice}
                      </TableCell>
                      <TableCell sx={{ borderColor: theme.palette.divider }}>
                        {product.minBidPrice}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ 
              p: 3, 
              textAlign: "center",
              bgcolor: theme.palette.background.neutral,
              borderRadius: 2
            }}>
              <Typography color="text.secondary">
                لا توجد سلع متوفرة
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

const DetailItem = ({ label, value, icon }) => {
  const theme = useTheme();

  return (
    <Box sx={{ 
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2,
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: theme.palette.background.neutral
    }}>
      <Avatar sx={{ 
        bgcolor: theme.palette.background.default,
        color: theme.palette.text.primary,
        width: 40,
        height: 40
      }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="body1" fontWeight="500">
          {value || "غير متوفر"}
        </Typography>
      </Box>
    </Box>
  );
};

export default AuctionDetails;